/**
 * Werkzeuge des Sprachgesprächs — aufgeteilt nach dem, was wo hingehört.
 *
 * Oberfläche (`ui_*`) läuft im Browser, weil nur er navigieren kann.
 * Alles, was Daten liest, geht an POST /voice/realtime/tool: dort hängt die
 * Mandantengrenze am Zugangstoken, nicht an einem Argument, das das Sprachmodell
 * frei wählen könnte.
 */
import api from '@/lib/axios';
import { setVoiceSearchCriteria, VOICE_SEARCH_EVENT } from '@/lib/voiceControl';

export const PAGE_PATHS = {
  students: '/students',
  teachers: '/teachers',
  guardians: '/guardians',
  courses: '/courses',
  families: '/families',
  invoices: '/invoices',
  enrollments: '/unterrichtsverwaltung',
  trial_lessons: '/trial-lessons',
  prepaid_cards: '/prepaid-cards',
  locations: '/locations',
  dashboard: '/',
  calendar: '/calendar',
  payers_without_bank: '/payer-accounts?filter=ohne-bank',
};

const SEARCH_PATH = PAGE_PATHS.enrollments;

/** Gesprochene Lücke, die die Oberfläche selbst öffnet — auch wenn das Modell ablehnt. */
export function pathForMissingData(transcript) {
  const n = String(transcript || '')
    .toLowerCase()
    .replace(/ü/g, 'ue')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ß/g, 'ss');
  const bank = [
    'ohne bankverbindung', 'keine bankverbindung', 'ohne bankdaten',
    'fehlende bankdaten', 'ohne iban', 'keine iban',
  ];
  if (bank.some((phrase) => n.includes(phrase))) return PAGE_PATHS.payers_without_bank;
  return null;
}

async function callBackendTool(name, args) {
  try {
    const { data } = await api.post('/voice/realtime/tool', { name, arguments: args || {} });
    return data;
  } catch (err) {
    // Als Ergebnis zurückgeben, nicht werfen: das Modell soll den Fehlschlag
    // aussprechen können, statt mitten im Satz zu verstummen.
    return { error: `Abfrage fehlgeschlagen (${err?.response?.status || 'Netzwerk'})` };
  }
}

/**
 * @param {Object} opts
 * @param {(path:string)=>void} opts.navigate
 * @param {()=>void} opts.stopVoice
 * @param {()=>string} opts.getPathname
 */
export function createRealtimeToolRunner({ navigate, stopVoice, getPathname = () => '' }) {
  return async function runTool(name, args = {}) {
    switch (name) {
      case 'ui_open_page': {
        const path = PAGE_PATHS[args.page];
        if (!path) return { error: `Unbekannter Bereich: ${args.page}` };
        navigate?.(path);
        return { opened: path };
      }

      case 'ui_open_record': {
        const path = String(args.path || '');
        // Nur interne Pfade — ein Sprachmodell soll den Browser nicht nach außen schicken.
        if (!path.startsWith('/') || path.startsWith('//')) {
          return { error: 'Pfad nicht erlaubt' };
        }
        navigate?.(path);
        return { opened: path, label: args.label || '' };
      }

      case 'ui_stop_voice':
        stopVoice?.();
        return { stopped: true };

      case 'search_enrollments': {
        // Zählen macht das Backend, anzeigen der Browser — beides aus einem Aufruf,
        // damit gesprochene Zahl und sichtbare Liste nicht auseinanderlaufen.
        const result = await callBackendTool('search_enrollments', args);
        const criteria = result?.criteria || {};
        const mode = result?.mode === 'refine' ? 'refine' : 'new';
        if (Object.keys(criteria).length || mode === 'refine') {
          setVoiceSearchCriteria(criteria);
          if (getPathname() !== SEARCH_PATH) navigate?.(SEARCH_PATH);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(VOICE_SEARCH_EVENT, {
              detail: { criteria, mode },
            }));
          }
        }
        return result;
      }

      default:
        return callBackendTool(name, args);
    }
  };
}
